'use client';

import * as React from 'react';
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import { type VariantProps } from 'class-variance-authority';

import { cn } from 'cn';
import { toggleVariants } from '@/components/ui/toggle';

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
    orientation?: 'horizontal' | 'vertical';
  }
>({
  size: 'default',
  variant: 'default',
  spacing: 2,
  orientation: 'horizontal',
});

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 2,
  orientation = 'horizontal',
  children,
  ...props
}: ToggleGroupPrimitive.Props &
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
    orientation?: 'horizontal' | 'vertical';
  }) {
  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      data-orientation={orientation}
      data-vertical={orientation === 'vertical' ? '' : undefined}
      data-horizontal={orientation === 'horizontal' ? '' : undefined}
      orientation={orientation}
      style={{ '--gap': spacing } as React.CSSProperties}
      className={cn(
        'tw:group/toggle-group tw:flex tw:w-fit tw:flex-row tw:items-center tw:gap-[--spacing(var(--gap))] tw:rounded-lg tw:data-[spacing=0]:shadow-xs tw:data-disabled:opacity-50 tw:data-vertical:flex-col tw:data-vertical:items-stretch',
        className
      )}
      {...props}
    >
      <ToggleGroupContext.Provider
        value={{ variant, size, spacing, orientation }}
      >
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant = 'default',
  size = 'default',
  ...props
}: TogglePrimitive.Props & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={cn(
        'tw:shrink-0 tw:group-data-[spacing=0]/toggle-group:rounded-none tw:group-data-[spacing=0]/toggle-group:px-2 tw:group-data-[spacing=0]/toggle-group:shadow-none tw:focus:z-10 tw:focus-visible:z-10 tw:group-data-[spacing=0]/toggle-group:has-data-[icon=inline-end]:pr-1.5 tw:group-data-[spacing=0]/toggle-group:has-data-[icon=inline-start]:pl-1.5 tw:group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-lg tw:group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-lg tw:group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-lg tw:group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-lg tw:data-[state=on]:bg-muted tw:data-[state=on]:text-muted-foreground tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:border-l-0 tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:border-r-0 tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:border-t-0 tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:border-b-0 tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-l tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:last:border-r tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-t tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:last:border-b tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:hover:border-l tw:group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:hover:border-r tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:hover:border-t tw:group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:hover:border-b',
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}

export { ToggleGroup, ToggleGroupItem };
